import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { TResponse, TResponseWithQData } from "../../../types/common";
import axios from "../../../lib/axios";

export interface ILedgerEntry {
  id: number;
  company_id: number;
  client_id: number | null;
  client_name: string | null;
  invoice_id: number | null;
  invoice_number: string | null;
  entry_type: "credit" | "debit";
  category: string;
  amount: number;
  currency: string;
  reference: string | null;
  description: string | null;
  entry_date: string;
  is_reconciled: boolean;
  created_by_name: string | null;
  created_at: string;
}

const listLedger = (params: any) =>
  axios.get(API_ENDPOINTS.LEDGER_LIST, { params }).then((r) => r.data as TResponseWithQData<ILedgerEntry[]>);

const createLedgerEntry = (data: any) =>
  axios.post(API_ENDPOINTS.LEDGER_CREATE, data).then((r) => r.data as TResponse<ILedgerEntry>);

const updateLedgerEntry = (data: any) =>
  axios.post(API_ENDPOINTS.LEDGER_UPDATE, data).then((r) => r.data as TResponse<ILedgerEntry>);

const deleteLedgerEntry = (id: number) =>
  axios.post(API_ENDPOINTS.LEDGER_DELETE, { id }).then((r) => r.data as TResponse<null>);

export const useLedgerList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery<TResponseWithQData<ILedgerEntry[]>, Error>({
    queryKey: [API_ENDPOINTS.LEDGER_LIST, params],
    queryFn: () => listLedger(params),
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useLedgerCreate = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<ILedgerEntry>, Error, any>({
    mutationFn: createLedgerEntry,
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.LEDGER_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

export const useLedgerUpdate = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<ILedgerEntry>, Error, any>({
    mutationFn: updateLedgerEntry,
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.LEDGER_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};

export const useLedgerDelete = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation<TResponse<null>, Error, number>({
    mutationFn: deleteLedgerEntry,
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.LEDGER_LIST] }); options?.onSuccess?.(); },
    onError: options?.onError,
  });
};
